import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { FilterDropdown } from '@/components/search/FilterDropdown'
import { Checkbox } from '@/components/search/inputs/Checkbox'
import styles from '@/components/search/search.module.scss'
import { filterLabelPrettyName, filterClassName } from '@/lib/utilities/search'
import {
  selectSearchParametersFilterByParameter,
  selectSearchSliceByParameter,
} from '@/store/selectors/search'
import { isProd } from '@/lib/utilities/system'

type MultiSelectProps = {
  filter: string
  title: string
  supportingText?: {
    title: string
    subtitle?: string
  }
  toggleFilter: () => void
  isFilterOpen: boolean
  onChange: (params: { type: 'multi' | 'single'; filter: string; value: string | number }) => void
  hasAbbreviatedLabels?: boolean
  maximumNumberOfVisibleSelectedOptions?: number
}

export const LegacyMultiSelect = ({
  filter,
  title,
  supportingText,
  toggleFilter,
  isFilterOpen = false,
  onChange,
  hasAbbreviatedLabels = false,
  maximumNumberOfVisibleSelectedOptions = 3,
}: MultiSelectProps) => {
  const [mappedSelectedToAvailable, setMappedSelectedToAvailable] = useState<
    {
      id: number
      name: string
    }[]
  >([])
  const currentlySelectedOptions: (string | number)[] = useSelector(
    state => selectSearchParametersFilterByParameter(state, filter) || [],
  )
  const availableOptions: {
    id: number
    name: string
  }[] = useSelector(state => selectSearchSliceByParameter(state, filter) || [])

  useEffect(() => {
    setMappedSelectedToAvailable(
      () => availableOptions.filter(i => currentlySelectedOptions.includes(i.id)) || [],
    )
  }, [setMappedSelectedToAvailable, availableOptions, currentlySelectedOptions])

  return (
    <FilterDropdown
      title={title}
      toggleFilter={toggleFilter}
      isFilterOpen={isFilterOpen}
      hasSelectedOptions={mappedSelectedToAvailable.length > 0}
      maximumNumberOfVisibleSelectedOptions={maximumNumberOfVisibleSelectedOptions}
      selectedOptions={
        mappedSelectedToAvailable.length > 0
          ? mappedSelectedToAvailable.map(i =>
            filterLabelPrettyName(filter, i.name, undefined, hasAbbreviatedLabels),
          )
          : ['All']
      }>
      {availableOptions.length > 0 ? (
        <>
          <div className={styles['multi-select']}>
            {availableOptions.map(i => (
              <Checkbox
                key={i.id}
                valueAttribute="id"
                item={i}
                isSelected={currentlySelectedOptions.includes(i.id)}
                filter={filter}
                onChange={onChange}
                className={!isProd() ? `search__${filter}-button search__${filter}-button-${filterClassName(filterLabelPrettyName(filter, i.name))}` : ''}
              />
            ))}
          </div>
          {supportingText && (
            <div className={styles.supporting}>
              <p className={styles.title}>{supportingText.title}</p>
              {supportingText.subtitle && <p className={styles.subtitle}>{supportingText.subtitle}</p>}
            </div>
          )}
        </>
      ) : (
        <p className={styles['error-text']}>
          {`There are no "${title}" options available based on your current filter selections`}
        </p>
      )}
    </FilterDropdown>
  )
}
